import { AvatarDropdown, AvatarName, Footer, Question, SelectLang, TelegramButton } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { RequestConfig } from '@umijs/max';
const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';
import fixMenuItemIcon from '@/utils/fixMenuItemIcon';
import { getMenus } from '@/services/ant-design-pro/api';
import { notification } from 'antd';
import { stringify } from 'querystring';


/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<Question key="doc" />, <SelectLang key="SelectLang" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.username,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menu: {
      params: {
        user_id: initialState?.currentUser?.user_id,
      },
      request: async (params) => {
        if (!params.user_id) {
          return [];
        }
        const menuData = await getMenus(params);
        return fixMenuItemIcon(menuData);
      },
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <TelegramButton />
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */

const errorHandler = (error: any) => {
  const { response } = error;

  // 弹出错误提示
  notification.error({
    message: `请求错误 ${response?.status}`,
    description: response?.statusText || '网络错误，请稍后重试',
  });

  // 返回 Promise.reject() 以终止流程
  return Promise.reject(error);
};


export const request: RequestConfig = {
  ...errorConfig, // 保留已有的错误配置
  requestInterceptors: [
    (url, options) => {
      if (options.method.toUpperCase() === 'GET' || options.method.toUpperCase() === 'POST') {
        const filteredParams = Object.fromEntries(
          Object.entries(options.params || {}).filter(([_, v]) => v !== "")
        );

        const filteredData = Object.fromEntries(
          Object.entries(options.data || {}).filter(([_, v]) => v !== "")
        );

        return {
          url,
          options: {
            ...options,
            params: filteredParams, // 用过滤后的参数替换原有参数
            data: filteredData,     // 用过滤后的数据替换原有数据
          },
        };
      }
      return { url, options };
    },
  ],
  // 响应拦截器：统一处理错误
  responseInterceptors: [
    (response) => {
      // 检查业务逻辑错误
      if (response.data.retcode === -100) {
        const { search, pathname } = window.location;
        const urlParams = new URL(window.location.href).searchParams;
        /** 此方法会跳转到 redirect 参数所在的位置 */
        const redirect = urlParams.get('redirect');
        // Note: There may be security issues, please note
        if (window.location.pathname !== '/user/login' && !redirect) {
          history.replace({
            pathname: '/user/login',
            search: stringify({
              redirect: pathname + search,
            }),
          });
        }
        throw new Error(response.message || '请重新登录');
        // notification.error({
        //   message: `请重新登录 ${response?.retcode}`,
        //   description: response?.message || '请重新登录',
        // });
      } else if (response.data && response.data.retcode !== 0) {
        notification.error({
          message: `请求错误 ${response?.retcode}`,
          description: response?.message || '请求异常',
        });
      }
      return response; // 返回正常的数据
    },
  ],

  // 配置错误处理
  errorHandler,
};

